import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  useNotifications,
  useUnreadNotificationCount,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useClearAllNotifications,
  useDeleteNotification
} from "../../hooks/useNotifications";
import { useAcceptInvitation, useDeclineInvitation } from "../../hooks/useCollaboration";
import { useToastStore } from "../../stores/toastStore";
import type { Notification, NotificationType } from "../../types/orderbook";
import { IconButton } from "../IconButton";
import bellSimpleIcon from "../../icons/svg/bell-simple.svg";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

// Get icon for notification type
function getNotificationIcon(type: NotificationType): React.ReactNode {
  switch (type) {
    case "list_invitation":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 2L11 13" />
          <path d="M22 2l-7 20-4-9-9-4 20-7z" />
        </svg>
      );
    case "member_joined":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="8.5" cy="7" r="4" />
          <line x1="20" y1="8" x2="20" y2="14" />
          <line x1="23" y1="11" x2="17" y2="11" />
        </svg>
      );
    case "item_added":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <line x1="12" y1="8" x2="12" y2="16" />
          <line x1="8" y1="12" x2="16" y2="12" />
        </svg>
      );
    case "item_purchased":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      );
  }
}

interface InvitationNotificationItemProps {
  notification: Notification;
  onAccept: () => void;
  onDecline: () => void;
  isAccepting: boolean;
  isDeclining: boolean;
}

const InvitationNotificationItem: React.FC<InvitationNotificationItemProps> = ({
  notification,
  onAccept,
  onDecline,
  isAccepting,
  isDeclining
}) => {
  return (
    <div className={`ob-notification-item ob-notification-item--invitation ${!notification.is_read ? "ob-notification-item--unread" : ""}`}>
      <div className="ob-notification-icon">
        {getNotificationIcon(notification.type)}
      </div>
      <div className="ob-notification-content">
        <p className="ob-notification-message">{notification.message}</p>
        <span className="ob-notification-time">
          {dayjs(notification.created_at).fromNow()}
        </span>
        <div className="ob-notification-buttons">
          <button
            type="button"
            className="ob-notification-btn ob-notification-btn--accept"
            onClick={(e) => {
              e.stopPropagation();
              onAccept();
            }}
            disabled={isAccepting || isDeclining}
          >
            {isAccepting ? "Accepting..." : "Accept"}
          </button>
          <button
            type="button"
            className="ob-notification-btn ob-notification-btn--decline"
            onClick={(e) => {
              e.stopPropagation();
              onDecline();
            }}
            disabled={isAccepting || isDeclining}
          >
            {isDeclining ? "..." : "Decline"}
          </button>
        </div>
      </div>
      {!notification.is_read && <div className="ob-notification-dot" />}
    </div>
  );
};

interface NotificationItemProps {
  notification: Notification;
  onRead: (id: string) => void;
  onClick: (notification: Notification) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onRead,
  onClick
}) => {
  return (
    <button
      type="button"
      className={`ob-notification-item ${!notification.is_read ? "ob-notification-item--unread" : ""}`}
      onClick={() => {
        if (!notification.is_read) {
          onRead(notification.id);
        }
        onClick(notification);
      }}
    >
      <div className="ob-notification-icon">
        {getNotificationIcon(notification.type)}
      </div>
      <div className="ob-notification-content">
        <p className="ob-notification-message">{notification.message}</p>
        <span className="ob-notification-time">
          {dayjs(notification.created_at).fromNow()}
        </span>
      </div>
      {!notification.is_read && <div className="ob-notification-dot" />}
    </button>
  );
};

export const NotificationDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);
  const [processingInvitation, setProcessingInvitation] = useState<string | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showToast = useToastStore((s) => s.show);

  const { data: notifications = [], isLoading } = useNotifications();
  const unreadCount = useUnreadNotificationCount();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const clearAll = useClearAllNotifications();
  const deleteNotification = useDeleteNotification();
  const acceptInvitation = useAcceptInvitation();
  const declineInvitation = useDeclineInvitation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setMenuPosition(null);
      }
    };

    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    } else {
      setMenuPosition(null);
    }
  }, [isOpen]);

  const handleAcceptInvitation = async (notification: Notification) => {
    const invitationId = notification.metadata?.invitation_id as string | undefined;
    
    console.log("🔔 Accept invitation clicked");
    console.log("📋 Notification:", notification);
    console.log("🔑 Invitation ID from metadata:", invitationId);
    console.log("📦 Full metadata:", notification.metadata);
    
    if (!invitationId) {
      console.error("❌ No invitation_id found in notification metadata");
      return;
    }

    setProcessingInvitation(notification.id);
    try {
      console.log("🚀 Calling acceptInvitation.mutateAsync with token:", invitationId);
      const result = await acceptInvitation.mutateAsync(invitationId);
      console.log("✅ Accept invitation result:", result);
      
      if (result.success) {
        console.log("✅ Invitation accepted successfully, listId:", result.listId);
        // Delete the notification after accepting
        await deleteNotification.mutateAsync(notification.id);
        // Refresh lists to show the new list
        await queryClient.invalidateQueries({ queryKey: ["lists"] });
        // Close dropdown and navigate to the list
        setIsOpen(false);
        setMenuPosition(null);
        if (result.listId) {
          console.log("🧭 Navigating to list:", result.listId);
          navigate(`/list/${result.listId}`);
        } else {
          console.log("🧭 No listId, navigating to home");
          navigate("/");
        }
      } else {
        console.error("❌ Accept invitation failed:", result.error);
        showToast({
          type: "error",
          title: "Failed to accept invitation",
          description: result.error || "Unknown error occurred"
        });
      }
    } catch (error) {
      console.error("❌ Error accepting invitation:", error);
      const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred";
      showToast({
        type: "error",
        title: "Error accepting invitation",
        description: errorMessage
      });
    } finally {
      setProcessingInvitation(null);
    }
  };

  const handleDeclineInvitation = async (notification: Notification) => {
    const invitationId = notification.metadata?.invitation_id as string | undefined;
    
    console.log("🔔 Decline invitation clicked");
    console.log("🔑 Invitation ID from metadata:", invitationId);
    
    if (!invitationId) {
      console.error("❌ No invitation_id found in notification metadata");
      return;
    }

    setProcessingInvitation(notification.id);
    try {
      console.log("🚀 Calling declineInvitation.mutateAsync with token:", invitationId);
      const result = await declineInvitation.mutateAsync(invitationId);
      console.log("✅ Decline invitation result:", result);
      
      if (result.success) {
        // Delete the notification after declining
        await deleteNotification.mutateAsync(notification.id);
      } else {
        console.error("❌ Decline invitation failed:", result.error);
        showToast({
          type: "error",
          title: "Failed to decline invitation",
          description: result.error || "Unknown error occurred"
        });
      }
    } catch (error) {
      console.error("❌ Error declining invitation:", error);
      const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred";
      showToast({
        type: "error",
        title: "Error declining invitation",
        description: errorMessage
      });
    } finally {
      setProcessingInvitation(null);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    setIsOpen(false);
    setMenuPosition(null);

    // Navigate based on notification type
    if (notification.list_id) {
      navigate(`/list/${notification.list_id}`);
    }
  };

  return (
    <div className="ob-notification-container">
      <IconButton
        ref={buttonRef}
        icon={bellSimpleIcon}
        alt="Notifications"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
        aria-expanded={isOpen}
      >
        {unreadCount > 0 && (
          <span className="ob-notification-badge">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </IconButton>

      {isOpen && menuPosition && createPortal(
        <div
          ref={menuRef}
          className="ob-notification-dropdown"
          style={{
            position: "fixed",
            top: `${menuPosition.top}px`,
            right: `${menuPosition.right}px`
          }}
        >
          <div className="ob-notification-header">
            <h3 className="ob-notification-title">Notifications</h3>
            {notifications.length > 0 && (
              <div className="ob-notification-actions">
                {unreadCount > 0 && (
                  <button
                    type="button"
                    className="ob-notification-action"
                    onClick={() => markAllRead.mutate()}
                    disabled={markAllRead.isPending}
                  >
                    Mark all read
                  </button>
                )}
                <button
                  type="button"
                  className="ob-notification-action ob-notification-action--muted"
                  onClick={() => clearAll.mutate()}
                  disabled={clearAll.isPending}
                >
                  Clear all
                </button>
              </div>
            )}
          </div>

          <div className="ob-notification-list">
            {isLoading ? (
              <div className="ob-notification-empty">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="ob-notification-empty">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ opacity: 0.3 }}
                >
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => 
                notification.type === "list_invitation" ? (
                  <InvitationNotificationItem
                    key={notification.id}
                    notification={notification}
                    onAccept={() => handleAcceptInvitation(notification)}
                    onDecline={() => handleDeclineInvitation(notification)}
                    isAccepting={processingInvitation === notification.id && acceptInvitation.isPending}
                    isDeclining={processingInvitation === notification.id && declineInvitation.isPending}
                  />
                ) : (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onRead={(id) => markRead.mutate(id)}
                    onClick={handleNotificationClick}
                  />
                )
              )
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
